import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TextField, Button, Typography, Paper, Select, MenuItem } from '@mui/material';
import Grid from '@mui/material/Grid2';
import axios from 'axios';

const apiUrlModificarGrupo = process.env.REACT_APP_API_GRUPO; // Endpoint base para modificaciones

function EditarReserva() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reserva, setReserva] = useState(null);
  const [loading, setLoading] = useState(true);

  // Cargar la reserva completa (incluye miembros) y guardar una copia original
  useEffect(() => {
    const fetchReserva = async () => {
      try {
        const response = await axios.get(`${apiUrlModificarGrupo}/${id}`);
        console.log("Respuesta de la API:", response.data);
        setReserva(response.data);// Guardamos la versión original
      } catch (error) {
        console.error('Error al cargar la reserva:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchReserva();
  }, [id]);

  // Función para actualizar campos generales de la reserva
  const handleChange = (field, value) => {
    setReserva({ ...reserva, [field]: value });
  };

  // Función para actualizar campos de cada miembro
  const handleMemberChange = (index, field, value) => {
    const updatedMembers = reserva.miembros.map((member, idx) =>
      idx === index ? { ...member, [field]: value } : member
    );
    setReserva({ ...reserva, miembros: updatedMembers });
  };

  const handleResponsableChange = (field, value) => {
    const updatedMiembros = reserva.miembros.map((m) =>
      m.es_responsable ? { ...m, [field]: value } : m
    );
    setReserva({ ...reserva, miembros: updatedMiembros });
  };
  

  // Obtener el registro del responsable (se asume que existe uno marcado con es_responsable true)
  const responsableCompleto = reserva?.miembros?.find(member => member.es_responsable) || {};

  // Manejar la actualización completa de la reserva fusionando valores
  const handleSave = async () => {
    const responsable = reserva.miembros.find(m => m.es_responsable);
    const miembrosSinResponsable = reserva.miembros.filter(m => !m.es_responsable);
  
    const mapDomicilio = (m) => ({
      ...m,
      genero: m.genero ?? '',
      domicilio: {
        calle: m.calle ?? '',
        numero: m.numero ?? '',
        cp: m.cp ?? '',
        provincia: m.provincia ?? '',
        pais: m.pais ?? '',
      }
    });
  
    const payload = {
      responsable: mapDomicilio(responsable),
      miembros: miembrosSinResponsable.map(mapDomicilio),
      fechaEntrada: reserva.fecha_entrada ?? '',
      fechaSalida: reserva.fecha_salida ?? '',
      horaEntrada: reserva.hora_entrada ?? '',
      horaSalida: reserva.hora_salida ?? '',
      usuario_creador_id: reserva.usuario_creador_id,
      reserva_qloapp: reserva.reserva_qloapp ?? null,
    };
  
    try {
      const cleanedPayload = JSON.parse(JSON.stringify(payload)); // Elimina undefineds
      await axios.put(`${apiUrlModificarGrupo}/actualizar-reserva/${reserva.id}`, cleanedPayload);
      navigate('/');
    } catch (error) {
      console.error('Error al actualizar la reserva:', error);
      alert('Error al actualizar la reserva.');
    }
  };  
  

  if (loading) {
    return <Typography>Cargando datos de la reserva...</Typography>;
  }

  if (!reserva) {
    return <Typography>No se encontró la reserva.</Typography>;
  }

  return (
    <Paper sx={{ p: 4, maxWidth: 1200, margin: '20px auto' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Editar Reserva #{reserva.id}
      </Typography>
      <Grid container spacing={2}>
        {/* Datos generales */}
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Fecha Entrada"
            type="date"
            value={reserva.fecha_entrada ? reserva.fecha_entrada.substring(0, 10) : ''}
            onChange={(e) => handleChange('fecha_entrada', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Fecha Salida"
            type="date"
            value={reserva.fecha_salida ? reserva.fecha_salida.substring(0, 10) : ''}
            onChange={(e) => handleChange('fecha_salida', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        {/* Datos del Responsable */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
            Datos del Responsable
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Nombre Responsable"
            value={responsableCompleto?.nombre ?? ''}
            onChange={(e) => {
              const updatedMembers = reserva.miembros.map(member =>
                member.es_responsable ? { ...member, nombre: e.target.value } : member
              );
              setReserva({ ...reserva, miembros: updatedMembers });
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Apellido Responsable"
            value={responsableCompleto?.apellido ?? ''}
            onChange={(e) => {
              const updatedMembers = reserva.miembros.map(member =>
                member.es_responsable ? { ...member, apellido: e.target.value } : member
              );
              setReserva({ ...reserva, miembros: updatedMembers });
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="OI Responsable"
            value={responsableCompleto?.oi ?? ''}
            onChange={(e) => {
              const updatedMembers = reserva.miembros.map(member =>
                member.es_responsable ? { ...member, oi: e.target.value } : member
              );
              setReserva({ ...reserva, miembros: updatedMembers });
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="DNI Responsable"
            value={responsableCompleto?.dni ?? ''}
            onChange={(e) => {
              const updatedMembers = reserva.miembros.map(member =>
                member.es_responsable ? { ...member, dni: e.target.value } : member
              );
              setReserva({ ...reserva, miembros: updatedMembers });
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Edad Responsable"
            value={responsableCompleto?.edad ?? ''}
            onChange={(e) => {
              const updatedMembers = reserva.miembros.map(member =>
                member.es_responsable ? { ...member, edad: e.target.value } : member
              );
              setReserva({ ...reserva, miembros: updatedMembers });
            }}
          />
        </Grid>
        <Grid item xs={3}> 
          <Select
            fullWidth
            value={responsableCompleto?.genero ?? ''}
            onChange={(e) => handleResponsableChange('genero', e.target.value)}
            displayEmpty
          >
            <MenuItem value="" disabled>Seleccionar género</MenuItem>
            <MenuItem value="MASCULINO">Masculino</MenuItem>
            <MenuItem value="FEMENINO">Femenino</MenuItem>
            <MenuItem value="NO_ESPECIFICADO">No especificado</MenuItem>
          </Select>
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Federación/Deporte"
            value={responsableCompleto?.federacion_deporte ?? ''}
            onChange={(e) => {
              const updatedMembers = reserva.miembros.map(member =>
                member.es_responsable ? { ...member, federacion_deporte: e.target.value } : member
              );
              setReserva({ ...reserva, miembros: updatedMembers });
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Rol Responsable"
            value={responsableCompleto?.rol ?? ''}
            onChange={(e) => {
              const updatedMembers = reserva.miembros.map(member =>
                member.es_responsable ? { ...member, rol: e.target.value } : member
              );
              setReserva({ ...reserva, miembros: updatedMembers });
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Tipo de Alojado Responsable"
            value={responsableCompleto?.tipoalojado ?? ''}
            onChange={(e) => {
              const updatedMembers = reserva.miembros.map(member =>
                member.es_responsable ? { ...member, tipoalojado: e.target.value } : member
              );
              setReserva({ ...reserva, miembros: updatedMembers });
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Movilidad Reducida"
            value={responsableCompleto?.movilidad_reducida ?? ''}
            onChange={(e) => {
              const updatedMembers = reserva.miembros.map(member =>
                member.es_responsable ? { ...member, movilidad_reducida: e.target.value } : member
              );
              setReserva({ ...reserva, miembros: updatedMembers });
            }}
          />
        </Grid>

        {/* Domicilio del Responsable */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mt: 4, mb: 2 }}>
            Domicilio del Responsable
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Calle"
            value={responsableCompleto?.calle || ''}
            onChange={(e) => {
              const updatedMembers = reserva.miembros.map(member =>
                member.es_responsable ? { ...member, calle: e.target.value } : member
              );
              setReserva({ ...reserva, miembros: updatedMembers });
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            fullWidth
            label="Número"
            value={responsableCompleto?.numero || ''}
            onChange={(e) => {
              const updatedMembers = reserva.miembros.map(member =>
                member.es_responsable ? { ...member, numero: e.target.value } : member
              );
              setReserva({ ...reserva, miembros: updatedMembers });
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            fullWidth
            label="Código Postal"
            value={responsableCompleto?.cp ?? ''}
            onChange={(e) => {
              const updatedMembers = reserva.miembros.map(member =>
                member.es_responsable ? { ...member, cp: e.target.value } : member
              );
              setReserva({ ...reserva, miembros: updatedMembers });
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Provincia"
            value={responsableCompleto?.provincia ?? ''}
            onChange={(e) => {
              const updatedMembers = reserva.miembros.map(member =>
                member.es_responsable ? { ...member, provincia: e.target.value } : member
              );
              setReserva({ ...reserva, miembros: updatedMembers });
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="País"
            value={responsableCompleto?.pais ?? ''}
            onChange={(e) => {
              const updatedMembers = reserva.miembros.map(member =>
                member.es_responsable ? { ...member, pais: e.target.value } : member
              );
              setReserva({ ...reserva, miembros: updatedMembers });
            }}
          />
        </Grid>

        {/* Sección de Miembros (excluyendo al responsable) */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
            Miembros
          </Typography>
          {reserva.miembros && reserva.miembros
            .filter(member => !member.es_responsable)
            .map((member, index) => (
              <Grid container spacing={2} key={member.id || index} sx={{ mb: 2, borderBottom: '1px solid #ccc', pb: 2 }}>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Nombre"
                    value={member.nombre || ''}
                    onChange={(e) => handleMemberChange(index, 'nombre', e.target.value)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Apellido"
                    value={member.apellido || ''}
                    onChange={(e) => handleMemberChange(index, 'apellido', e.target.value)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="DNI"
                    value={member.dni || ''}
                    onChange={(e) => handleMemberChange(index, 'dni', e.target.value)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Edad"
                    value={member.edad || ''}
                    onChange={(e) => handleMemberChange(index, 'edad', e.target.value)}
                  />
                </Grid>
                <Grid item xs={3}>
                <Select
                  fullWidth
                  value={member.genero ?? ''} // o miembro.sexo si aún estás usando ese campo
                  onChange={(e) => handleMemberChange(index, 'genero', e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>Seleccionar género</MenuItem>
                  <MenuItem value="MASCULINO">Masculino</MenuItem>
                  <MenuItem value="FEMENINO">Femenino</MenuItem>
                  <MenuItem value="NO_ESPECIFICADO">No especificado</MenuItem>
                </Select>
              </Grid>

                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Movilidad"
                    value={member.movilidad_reducida || ''}
                    onChange={(e) => handleMemberChange(index, 'movilidad_reducida', e.target.value)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Federación/Deporte"
                    value={member.federacion_deporte || ''}
                    onChange={(e) => handleMemberChange(index, 'federacion_deporte', e.target.value)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Rol"
                    value={member.rol ?? 'No corresponde'}
                    onChange={(e) => handleMemberChange(index, 'rol', e.target.value)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Tipo de Alojado"
                    value={member.tipoalojado ?? 'No corresponde'}
                    onChange={(e) => handleMemberChange(index, 'tipoalojado', e.target.value)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Responsable"
                    value={member.es_responsable ? 'Si' : 'No'}
                    disabled
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Calle"
                    value={member.calle || ''}
                    onChange={(e) => handleMemberChange(index, 'calle', e.target.value)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Número"
                    value={member.numero || ''}
                    onChange={(e) => handleMemberChange(index, 'numero', e.target.value)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="CP"
                    value={member.cp || ''}
                    onChange={(e) => handleMemberChange(index, 'cp', e.target.value)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Provincia"
                    value={member.provincia || ''}
                    onChange={(e) => handleMemberChange(index, 'provincia', e.target.value)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="País"
                    value={member.pais || ''}
                    onChange={(e) => handleMemberChange(index, 'pais', e.target.value)}
                  />
                </Grid>
              </Grid>
            ))}
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item>
          <Button variant="contained" onClick={handleSave}>
            Guardar Cambios
          </Button>
        </Grid>
        <Grid item>
          <Button variant="outlined" onClick={() => navigate('/')}>
            Cancelar
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default EditarReserva;
